import { Searchbar } from './searchbar/searchbar';
import { useDispatch, useSelector } from 'react-redux';
import { ChangeEvent, KeyboardEvent, MouseEvent, useEffect, useState, Fragment } from 'react';
import { productsActions, productsSelectors } from '../../store/products';
import { DataGrid } from './data-grid/data-grid';
import { SelectChangeEvent } from '@mui/material';
import { DashboardContainer, TitleTypography } from './inventory-dashboard.styles';
import { useSearchParams } from 'react-router-dom';
import { RootState } from '../../store/store';
import { singleProductActions } from '../../store/single-product';

export function InventoryDashboard(): JSX.Element {
  const [lastRowRef, setLastRowRef] = useState<HTMLTableRowElement | null>(null);

  const [firstRowRef, setFirstRowRef] = useState<HTMLTableRowElement | null>(null);

  const [, setSearchParams] = useSearchParams();

  const dispatch = useDispatch();

  const queryParams = useSelector((state: RootState) =>
    productsSelectors.selectQueryParams(state.products)
  );

  const searchText = useSelector((state: RootState) =>
    productsSelectors.selectSearchbarString(state.products)
  );

  function fetchProducts() {
    dispatch(productsActions.getProducts());
  }

  function handleRowClick(productId: string) {
    dispatch(singleProductActions.getProductById(productId));
  }

  function fetchMoreProducts() {
    dispatch(productsActions.getMoreProducts());
  }

  function fetchGeneralConfig() {
    dispatch(productsActions.getGeneralConfig());
  }

  // UseEffect that runs ones when we load the for the first time the inventory
  useEffect(() => {
    fetchProducts();
    fetchGeneralConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // UseEffect that runs every time we want to load more results only
  useEffect(() => {
    if (lastRowRef) {
      lastRowRef.scrollIntoView({ block: 'end' });
    }
  }, [lastRowRef]);

  // useEffect that runs every time a new search runs (a query of up to 100 products)
  useEffect(() => {
    if (firstRowRef) {
      firstRowRef.scrollIntoView(false);
      document.documentElement.scrollTo(0, 0);
    }
  }, [firstRowRef]);

  useEffect(() => {
    const params = new URLSearchParams(
      Object.entries(queryParams).filter(([, val]) => val) as string[][]
    );
    setSearchParams(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  function handleTenantAndLocationChange(event: SelectChangeEvent<string>) {
    var newTenant: string | undefined;
    if (event.target.name === 'tenantId') {
      newTenant = event.target.value === 'All' ? '' : event.target.value;
    } else {
      newTenant = queryParams.tenantId;
    }

    let newLocation: string | undefined;
    if (event.target.name === 'locationId') {
      newLocation = event.target.value === 'All' ? '' : event.target.value;
    } else {
      newLocation = '';
    }

    dispatch(
      productsActions.updateQueryParams({
        tenantId: newTenant,
        locationId: newLocation,
        limit: 100,
        skip: 0
      })
    );
    fetchProducts();
  }

  function handleSearch(event: MouseEvent | ChangeEvent | KeyboardEvent) {
    event.preventDefault();
    dispatch(
      productsActions.updateQueryParams({
        searchString: searchText,
        limit: 100,
        skip: 0
      })
    );
    fetchProducts();
  }

  function handleMoreResultsButtonClick() {
    const currentLimit = queryParams.limit ?? 100;
    // A check for pasting a URL with more than 100 products and then pressing load more
    const newSkip =
      queryParams.skip !== undefined ? Math.abs(100 - currentLimit) + queryParams.skip + 100 : 100;

    dispatch(productsActions.updateQueryParams({ limit: 100, skip: newSkip }));
    fetchMoreProducts();
  }

  function handleSearchFilterChange(event: SelectChangeEvent<string>) {
    dispatch(
      productsActions.updateQueryParams({
        limit: 100,
        skip: 0,
        searchFilter: event.target.value
      })
    );

    fetchProducts();
  }

  return (
    <DashboardContainer>
      <Fragment>
        <TitleTypography>Product Inventory</TitleTypography>
        <Searchbar onSearch={handleSearch} />
        <DataGrid
          onChange={handleTenantAndLocationChange}
          onMoreResultsButtonClick={handleMoreResultsButtonClick}
          setLastRowRef={setLastRowRef}
          setFirstRowRef={setFirstRowRef}
          onRowClick={handleRowClick}
          handleSearchFilterChange={handleSearchFilterChange}
        />
      </Fragment>
    </DashboardContainer>
  );
}
